import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { simplefinApi } from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Building2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Wallet,
  Clock
} from 'lucide-react'

interface BankAccount {
  id: number
  name: string
  institution: string
  account_type: string
  balance: number
  available_balance: number
  currency: string
  last_synced: string | null
  is_active: boolean
}

interface SyncStatus {
  configured: boolean
  accounts: BankAccount[]
  last_import: string | null
}

interface SyncResult {
  accounts: { created: number; updated: number }
  transactions: { created: number; skipped: number }
  synced_at: string
}

export default function BankAccounts() {
  const queryClient = useQueryClient()

  const { data: status, isLoading: statusLoading } = useQuery<SyncStatus>({
    queryKey: ['simplefin-status'],
    queryFn: async () => {
      const res = await simplefinApi.getStatus()
      return res.data
    },
  })

  const { data: accounts = [] } = useQuery<BankAccount[]>({
    queryKey: ['bank-accounts'],
    queryFn: async () => {
      const res = await simplefinApi.getAccounts()
      return res.data
    },
  })

  const syncMutation = useMutation<SyncResult>({
    mutationFn: async () => {
      const res = await simplefinApi.fullSync(30)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['simplefin-status'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['spending-summary'] })
    },
  })

  const totalBalance = accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0)

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!status?.configured) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bank Accounts</h1>
          <p className="text-muted-foreground">Connect your bank accounts via SimpleFIN</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              SimpleFIN Not Configured
            </CardTitle>
            <CardDescription>
              Add your SimpleFIN access URL to the environment to enable bank sync
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Set the <code className="bg-muted px-1 rounded">SIMPLEFIN_ACCESS_URL</code> environment
              variable in your deployment to connect your bank accounts.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bank Accounts</h1>
          <p className="text-muted-foreground">
            {status.last_import
              ? `Last synced: ${new Date(status.last_import).toLocaleString()}`
              : 'Never synced'}
          </p>
        </div>
        <Button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          {syncMutation.isPending ? 'Syncing...' : 'Sync Now'}
        </Button>
      </div>

      {/* Sync Result */}
      {syncMutation.isSuccess && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Sync completed successfully</span>
            </div>
            <div className="mt-2 text-sm text-green-600 dark:text-green-400">
              Accounts: {syncMutation.data.accounts.created} created, {syncMutation.data.accounts.updated} updated
              <br />
              Transactions: {syncMutation.data.transactions.created} imported, {syncMutation.data.transactions.skipped} skipped (duplicates)
            </div>
          </CardContent>
        </Card>
      )}

      {syncMutation.isError && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Sync failed</span>
            </div>
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              {(syncMutation.error as any)?.response?.data?.error || 'Unknown error'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Total Balance Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Balance</CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{formatCurrency(totalBalance)}</div>
          <p className="text-xs text-muted-foreground">
            Across {accounts.length} account{accounts.length !== 1 ? 's' : ''}
          </p>
        </CardContent>
      </Card>

      {/* Accounts List */}
      <div className="grid gap-4 md:grid-cols-2">
        {accounts.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="pt-6 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No accounts synced yet. Click "Sync Now" to import your bank accounts.
              </p>
            </CardContent>
          </Card>
        ) : (
          accounts.map((account) => (
            <Card key={account.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{account.name}</CardTitle>
                <CardDescription className="flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {account.institution}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-muted-foreground">Balance</span>
                    <span className="text-2xl font-bold">{formatCurrency(account.balance)}</span>
                  </div>
                  {account.available_balance !== account.balance && (
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm text-muted-foreground">Available</span>
                      <span className="text-lg">{formatCurrency(account.available_balance)}</span>
                    </div>
                  )}
                  {account.last_synced && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground pt-2">
                      <Clock className="h-3 w-3" />
                      Last updated: {new Date(account.last_synced).toLocaleString()}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}
